'use client';

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';

import Typography from '../Typography/Typography';
import Iconography from '../Iconography/Iconography';
import Box from '../Box/Box';
import Checkbox from '../Checkbox/Checkbox';
import { dataLayerHandler } from '../../../utils/analytics';

import styles from './Dropdown.module.scss';

export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownGroupOption {
  groupLabel: string;
  data: DropdownOption[];
}

export interface ValidationResponse {
  validationType?: 'success' | 'warning' | 'error';
  message?: string;
}

export interface DropdownProps {
  theme?: 'dark' | 'light';
  /**
   * Currently supports only the list variant
   */
  variant?: 'list' | 'checkbox' | 'countryCode' | 'withSubitem';
  /**
   * TODO: Update size small and large after figma is updated
   */
  size?: 'small' | 'medium' | 'large';
  name?: string;
  register?: (name: string) => Record<string, unknown>;
  label?: string;
  helperText?: string;
  options: (DropdownOption | DropdownGroupOption)[];
  selectedOption?: string;
  /**
   * Array of selected values for multiselect mode
   */
  selectedOptions?: string[];
  placeholder?: string;
  handleSelect?: (value: string) => void;
  /**
   * Callback for multiselect mode - receives array of selected values
   */
  handleMultiSelect?: (values: string[]) => void;
  direction?: 'ltr' | 'rtl';
  /**
   * Validation response object containing error/warning/success state and message.
   */
  validationResponse?: ValidationResponse;
  /**
   * Whether to show the validation message area.
   * @default true
   */
  showValidation?: boolean;
  /**
   * Whether to show selected items text below the dropdown (for multiselect mode).
   * @default false
   */
  showSelectedText?: boolean;
  /**
   * Label prefix for selected items text (e.g., "Selected:").
   * @default "Selected:"
   */
  selectedTextLabel?: string;
  gaComponent?: string;
  required?: boolean;
  disabled?: boolean;
  inline?: boolean;
  logEvents?: boolean;
  /**
   * Enable multiselect mode with checkboxes
   */
  multiSelect?: boolean;
  /**
   * Whether the dropdown is rendered within a slider context (uses higher z-index)
   * @default false
   */
  inSlider?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  theme = 'dark',
  variant: _variant = 'list', // eslint-disable-line @typescript-eslint/no-unused-vars
  size = 'medium',
  name = 'dropdown',
  register,
  label,
  helperText,
  options,
  selectedOption,
  selectedOptions = [],
  placeholder,
  handleSelect,
  handleMultiSelect,
  direction = 'ltr',
  validationResponse,
  showValidation = true,
  showSelectedText = false,
  selectedTextLabel = 'Selected:',
  gaComponent = 'dropdown',
  required = false,
  disabled,
  inline = false,
  logEvents = true,
  multiSelect = false,
  inSlider = false,
}) => {
  const [selectedOptionLabel, setSelectedOptionLabel] = useState<string | undefined>(placeholder);
  const [selectedOptionId, setSelectedOptionId] = useState<string>(''); // Tracks the currently selected option for keyboard navigation.
  const [focusedOptionId, setFocusedOptionId] = useState<string>(''); // Tracks the currently focused option for keyboard navigation.
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [shouldOpenAbove, setShouldOpenAbove] = useState<boolean>(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  }>({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 445,
  });
  const [searchBuffer, setSearchBuffer] = useState<string>('');
  const [multiSelectedValues, setMultiSelectedValues] = useState<string[]>(
    Array.isArray(selectedOptions) ? selectedOptions : []
  );
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLUListElement>(null);

  // Check available space and calculate position when dropdown opens
  useEffect(() => {
    if (!isMenuOpen || !dropdownButtonRef.current) return;

    const calculatePosition = () => {
      const buttonRect = dropdownButtonRef.current?.getBoundingClientRect();
      if (!buttonRect) return;

      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const padding = 16; // Padding from viewport edge
      const estimatedDropdownHeight = 250; // Approximate height for dropdown menu

      const openAbove =
        spaceBelow < estimatedDropdownHeight && spaceAbove > estimatedDropdownHeight;
      setShouldOpenAbove(openAbove);

      // Calculate max height based on available space
      const availableSpace = openAbove ? spaceAbove - padding : spaceBelow - padding;
      const maxHeight = Math.min(445, Math.max(200, availableSpace)); // Min 200px, max 445px

      // Calculate absolute position for portal
      setMenuPosition({
        top: openAbove ? buttonRect.top - 12 : buttonRect.bottom + 12,
        left: buttonRect.left,
        width: buttonRect.width,
        maxHeight,
      });
    };

    const handleScroll = (e: Event) => {
      // Allow scrolling within the dropdown menu itself
      const target = e.target as HTMLElement;
      const dropdownMenu = dropdownMenuRef.current;

      // Check if the scroll is happening on the dropdown menu or any of its children
      if (dropdownMenu && (target === dropdownMenu || dropdownMenu.contains(target))) {
        return; // Allow scrolling within dropdown
      }

      // Close dropdown when scrolling outside of it
      setIsMenuOpen(false);
    };

    const handleResize = () => {
      calculatePosition();
    };

    calculatePosition();

    // Close on scroll (except within dropdown), recalculate on resize
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMenuOpen]);

  // Update multiSelectedValues when selectedOptions prop changes
  useEffect(() => {
    // Guard against non-array selectedOptions
    if (!Array.isArray(selectedOptions)) {
      setMultiSelectedValues([]);
      return;
    }
    // Only update if the arrays are actually different to prevent infinite loops
    const areEqual =
      selectedOptions.length === multiSelectedValues.length &&
      selectedOptions.every((val, idx) => val === multiSelectedValues[idx]);
    if (!areEqual) {
      setMultiSelectedValues(selectedOptions);
    }
  }, [selectedOptions, multiSelectedValues]);

  // Get display text for multiselect mode
  const getMultiSelectDisplayText = (): string => {
    if (multiSelectedValues.length === 0) return placeholder || '';
    if (multiSelectedValues.length === 1) {
      // Find the label for the single selected value
      for (const option of options) {
        if ('value' in option && option.value === multiSelectedValues[0]) {
          return option.label;
        }
        if ('groupLabel' in option) {
          const found = option.data.find((d) => d.value === multiSelectedValues[0]);
          if (found) return found.label;
        }
      }
    }
    return `${multiSelectedValues.length} selected`;
  };

  // Get labels for all selected values (for showSelectedText display)
  const getSelectedLabels = (): string[] => {
    return multiSelectedValues.map((value) => {
      for (const option of options) {
        if ('value' in option && option.value === value) {
          return option.label;
        }
        if ('groupLabel' in option) {
          const found = option.data.find((d) => d.value === value);
          if (found) return found.label;
        }
      }
      return value;
    });
  };

  const handleMultiSelectItemClick = (value: string, label: string) => {
    const newValues = multiSelectedValues.includes(value)
      ? multiSelectedValues.filter((v) => v !== value)
      : [...multiSelectedValues, value];

    setMultiSelectedValues(newValues);
    logEvents &&
      dataLayerHandler({
        interactionType: 'dropdown-multiselect',
        gaComponent,
        label,
      });
    handleMultiSelect?.(newValues);
  };

  const handleMenuItemClick = (value: string, label: string, id: string) => {
    setIsMenuOpen(false);
    setSelectedOptionLabel(label);
    setSelectedOptionId(id);
    logEvents &&
      dataLayerHandler({
        interactionType: 'dropdown',
        gaComponent,
        label,
      });
    handleSelect?.(value);
  };

  const handleButtonClick = () => {
    if (!disabled) {
      setIsMenuOpen((prevState) => !prevState);
    }
  };

  const handleTypeAheadSearch = (key: string) => {
    const char = key.toLowerCase();
    if (!/^[a-z0-9 ]$/i.test(char)) return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const updatedBuffer = searchBuffer + char;
    setSearchBuffer(updatedBuffer);

    searchTimeoutRef.current = setTimeout(() => {
      setSearchBuffer('');
    }, 500);

    const optionsArray = [...(dropdownMenuRef?.current?.querySelectorAll("[role='option']") || [])];
    const match = optionsArray.find((option) =>
      option.textContent?.toLowerCase().startsWith(updatedBuffer)
    );
    if (match) {
      const matchedId = match.id;
      setFocusedOptionId(matchedId);
      match.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  // Moves selection on ArrowUp/ArrowDown.
  const navigateOptions = (key: string, optionsArray: Element[], currentIndex: number) => {
    const nextIndex =
      key === 'ArrowDown'
        ? currentIndex < optionsArray.length - 1
          ? currentIndex + 1
          : 0 // Loop to first
        : currentIndex > 0
          ? currentIndex - 1
          : optionsArray.length - 1; // Loop to last
    setFocusedOptionId(optionsArray?.[nextIndex]?.id);
    const selectedElement = dropdownMenuRef.current?.querySelector(
      `#${optionsArray?.[nextIndex]?.id}`
    ) as HTMLElement;
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  // Confirms selection when pressing Enter.
  const confirmSelection = (optionsArray: Element[], currentIndex: number) => {
    const selectedElement = optionsArray[currentIndex];
    if (selectedElement) {
      const selectedValue = selectedElement?.getAttribute('data-value');
      const selectedLabel = selectedElement?.textContent;
      const selectedId = selectedElement?.id;
      if (selectedValue && selectedLabel && selectedId) {
        handleMenuItemClick(selectedValue, selectedLabel, selectedId);
      }
    }
  };

  // Handles keyboard interactions for the dropdown, ensuring accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!isMenuOpen) {
      const openKeys = ['Enter', ' ', 'ArrowDown', 'ArrowUp'];
      if (openKeys.includes(e.key)) {
        e.preventDefault();
        setIsMenuOpen(true);
      }
    } else {
      const optionsArray = [
        ...(dropdownMenuRef?.current?.querySelectorAll("[role='option']") || []),
      ];
      if (optionsArray.length <= 0) return;
      const currentIndex = optionsArray.findIndex((option) =>
        focusedOptionId ? option.id === focusedOptionId : option.id === selectedOptionId
      );

      switch (e.key) {
        case ' ':
        case 'Escape':
        case 'Tab':
          e.preventDefault();
          setIsMenuOpen(false);
          break;

        case 'Enter': {
          e.preventDefault();
          confirmSelection(optionsArray, currentIndex);
          setIsMenuOpen(false);
          break;
        }

        case 'ArrowUp':
        case 'ArrowDown':
          e.preventDefault();
          navigateOptions(e.key, optionsArray, currentIndex);
          break;

        default:
          handleTypeAheadSearch(e.key);
          break;
      }
    }
  };

  // Updates the keyboard focused option when the menu opens, defaulting to the selected option.
  useEffect(() => {
    if (isMenuOpen) {
      setFocusedOptionId(selectedOptionId || '');
    } else {
      setFocusedOptionId('');
    }
  }, [isMenuOpen, selectedOptionId]);

  const handleDocumentClick = (e: MouseEvent) => {
    const target = e.target as Node;
    if (
      !dropdownButtonRef?.current?.contains(target) &&
      !dropdownMenuRef?.current?.contains(target)
    ) {
      setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    if (selectedOption) {
      let activeOption = '';
      let activeOptionId = '';
      for (let i = 0; i < options.length; i++) {
        const option = options[i];
        if ('value' in option && option?.value === selectedOption) {
          activeOption = option?.label;
          activeOptionId = `${name}-option-${i}`; // Generate ID
          break;
        } else if ('groupLabel' in option) {
          const data: DropdownOption[] = option?.data;
          for (let j = 0; j < data.length; j++) {
            if (data[j]?.value === selectedOption) {
              activeOption = data[j]?.label;
              const sanitizedGroupLabel = option?.groupLabel?.replace(/\s+/g, '-')?.toLowerCase();
              activeOptionId = `${name}-${sanitizedGroupLabel}-option-${j}`; // Generate ID
              break;
            }
          }
        }
      }
      setSelectedOptionLabel(activeOption);
      setSelectedOptionId(activeOptionId);
    }
  }, [selectedOption, options, name]);

  const constructOptionElement = (value: string, optionLabel: string, id: string) => {
    const isSelected = multiSelect
      ? multiSelectedValues.includes(value)
      : selectedOptionLabel === optionLabel;

    if (multiSelect) {
      return (
        <li
          key={id}
          id={id}
          tabIndex={0}
          className={classnames(styles.menuItem, styles.multiSelectItem, {
            [styles.active]: isSelected,
            [styles.focused]: focusedOptionId === id,
          })}
          role="option"
          aria-selected={isSelected}
          onClick={() => handleMultiSelectItemClick(value, optionLabel)}
          data-value={value}
        >
          <Checkbox
            theme={theme}
            isChecked={isSelected}
            handleChange={() => handleMultiSelectItemClick(value, optionLabel)}
            dataTestId={`${id}-checkbox`}
            gaComponent={`${gaComponent}-checkbox`}
            logEvents={false}
            showValidation={false}
          />
          <Typography
            className={classnames(styles.menuItemLabel, {
              [styles.selected]: isSelected,
            })}
            variant="body"
            size={size}
            direction={direction}
          >
            {optionLabel}
          </Typography>
        </li>
      );
    }

    return (
      <li
        key={id}
        id={id}
        tabIndex={0}
        className={classnames(styles.menuItem, {
          [styles.active]: isSelected,
          [styles.focused]: focusedOptionId === id,
        })}
        role="option"
        aria-selected={isSelected}
        onClick={() => handleMenuItemClick(value, optionLabel, id)}
        data-value={value}
      >
        <Typography
          className={classnames(styles.menuItemLabel, {
            [styles.selected]: isSelected,
          })}
          variant="body"
          size={size}
          direction={direction}
        >
          {optionLabel}
        </Typography>
      </li>
    );
  };

  return (
    <Box
      className={classnames(
        styles.dropdownMenuContainer,
        styles[`theme_${theme}`],
        styles[`size_${size}`],
        {
          [styles[`validation-${validationResponse?.validationType}`]]:
            validationResponse?.validationType,
        }
      )}
    >
      {(label || helperText) && (
        <Box mb={4}>
          {label && (
            <label htmlFor={name} id={`${name}-label`}>
              <Typography
                className={styles.label}
                variant="label"
                size="small"
                direction={direction}
                theme={theme}
                color="gray-3"
              >
                {label}
                {required && '*'}
              </Typography>
            </label>
          )}
          {helperText && (
            <Typography
              className={styles.helperText}
              variant="body"
              size="extraSmall"
              direction={direction}
              theme={theme}
              color="gray-5"
            >
              {helperText}
            </Typography>
          )}
        </Box>
      )}
      <Box
        ref={dropdownButtonRef}
        className={classnames(styles.dropdownMenuButton, {
          [styles.disabled]: disabled,
          [styles.noOutline]: inline,
        })}
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        aria-expanded={isMenuOpen}
        role="combobox"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-label={placeholder}
        aria-labelledby={`${name}-label ${name}-selected-text`}
        aria-controls={`${name}-listbox`}
        {...(isMenuOpen && focusedOptionId && { 'aria-activedescendant': focusedOptionId })}
      >
        <Typography
          id={`${name}-selected-text`}
          className={classnames(styles.selectedOption, {
            [styles.placeholder]: multiSelect
              ? multiSelectedValues.length === 0
              : selectedOptionLabel === placeholder,
            [styles.disabled]: disabled,
          })}
          variant="body"
          size={size}
          theme={theme}
          color={
            multiSelect
              ? multiSelectedValues.length > 0
                ? 'white'
                : disabled
                  ? 'gray-5'
                  : 'gray-3'
              : selectedOptionLabel !== placeholder
                ? 'white'
                : disabled
                  ? 'gray-5'
                  : 'gray-3'
          }
          direction={direction}
        >
          {multiSelect ? getMultiSelectDisplayText() : selectedOptionLabel}
        </Typography>
        <Box pl={4} direction={direction}>
          <Iconography
            className={classnames({
              [styles.dropdownIcon]: isMenuOpen,
            })}
            category="arrows"
            name="chevron-down"
            theme={theme}
            color="white"
            size={{ xs: 16, md: 20, lg: 24 }}
          />
        </Box>
      </Box>
      {isMenuOpen &&
        createPortal(
          <ul
            ref={dropdownMenuRef}
            className={classnames(
              styles.dropdownMenu,
              styles.portal,
              styles[`theme_${theme}`],
              styles[`size_${size}`],
              {
                [styles.inSlider]: inSlider,
              }
            )}
            // eslint-disable-next-line react/forbid-dom-props -- Required for dynamic portal positioning
            style={{
              position: 'fixed',
              top: shouldOpenAbove ? 'auto' : `${menuPosition.top}px`,
              bottom: shouldOpenAbove ? `${window.innerHeight - menuPosition.top}px` : 'auto',
              left: `${menuPosition.left}px`,
              width: `${menuPosition.width}px`,
              maxHeight: `${menuPosition.maxHeight}px`,
              overflowY: 'auto',
            }}
            role="listbox"
            id={`${name}-listbox`}
            aria-labelledby={`${name}-label`}
          >
            {options?.map((option, index) => {
              if ('groupLabel' in option) {
                return (
                  <React.Fragment key={index}>
                    <Typography
                      className={styles.groupLabel}
                      variant="label"
                      size={size}
                      theme={theme}
                      color="primary-1"
                      direction={direction}
                    >
                      {option?.groupLabel}
                    </Typography>
                    {option?.data.map(({ value, label }, subIndex) => {
                      const sanitizedGroupLabel = option?.groupLabel
                        ?.replace(/\s+/g, '-')
                        ?.toLowerCase();
                      return constructOptionElement(
                        value,
                        label,
                        `${name}-${sanitizedGroupLabel}-option-${subIndex}`
                      );
                    })}
                  </React.Fragment>
                );
              }
              const { value, label } = option;
              return constructOptionElement(value, label, `${name}-option-${index}`);
            })}
          </ul>,
          document.body
        )}
      {showSelectedText && multiSelect && (
        <Box mt={8}>
          <Typography
            variant="body"
            size="extraSmall"
            color="gray-5"
            theme={theme}
            direction={direction}
          >
            {selectedTextLabel}{' '}
            {multiSelectedValues.length > 0 ? getSelectedLabels().join(', ') : 'None'}
          </Typography>
        </Box>
      )}
      {showValidation && (
        <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
          {validationResponse?.message && (
            <Typography
              className={classnames(styles.validationMessage)}
              variant="body"
              size="extraSmall"
              direction={direction}
              theme={theme}
              id={`${name}-validation-message`}
            >
              {validationResponse.message || ''}
            </Typography>
          )}
        </Box>
      )}
      <input
        id={name}
        type="hidden"
        {...(register ? register(name) : {})}
        required={required}
        aria-describedby={`${name}-validation-message`}
      />
    </Box>
  );
};

export default Dropdown;
